import Vue from 'vue';
import axios from 'axios';
import decode from 'jwt-decode';
import {CLIENT_VERSION_HEADER, getClientBuildVersion} from '@/utils/api/versionApi';
import {reportConnectionFailure} from '@/utils/api/connectionFailure';
const http = axios.create({withCredentials: true});
let versionObserver = () => {}, isUpdatePaused = () => false;
export function configureSessionVersion(observer, paused) { versionObserver = observer; isUpdatePaused = paused; }
http.interceptors.request.use(config => {
    config.headers = Object.assign({}, config.headers, {[CLIENT_VERSION_HEADER]: getClientBuildVersion()});
    return config;
});
http.interceptors.response.use(response => { versionObserver(response); return response; }, error => {
    if (error.response) versionObserver(error.response);
    reportConnectionFailure(error);
    return Promise.reject(error);
});
const pending = {};
const exclusive = (scope, work) => navigator.locks ? navigator.locks.request('argus-refresh-' + scope, work) : work();
export const securityState = Vue.observable({locked: false, message: '', scope: 'staff', identity: null, pendingLogin: null});
export const authBase = scope => scope === 'platform' ? '/api/platform/auth' : '/auth';
export const tokenKey = scope => scope === 'platform' ? 'platformAccessToken' : 'token';
export const markerKey = scope => scope === 'platform' ? 'platformRefreshToken' : 'refreshToken';
export function claims(scope) {
    try { return decode(localStorage.getItem(tokenKey(scope)) || ''); } catch (_) { return null; }
}
export function identityOf(value) { return value ? `${value.aud || 'staff'}:${value.company || ''}:${value.userId || value.id || value.sub}` : null; }
export function lockSession(scope, error) {
    if (!claims(scope) && !securityState.identity) return;
    securityState.identity = securityState.identity || identityOf(claims(scope));
    securityState.scope = scope;
    securityState.message = error?.response?.data?.msg || 'Войдите снова, чтобы продолжить работу.';
    securityState.locked = true;
}
export function acceptSession(scope, session, login = false) {
    if (!session?.accessToken) return;
    const next = decode(session.accessToken);
    if (securityState.locked && securityState.scope === scope && securityState.identity !== identityOf(next)) {
        http.post(authBase(scope) + '/logout', {}).catch(() => {});
        throw new Error('Для продолжения открытой формы войдите в ту же учётную запись.');
    }
    localStorage.setItem(tokenKey(scope), session.accessToken);
    localStorage.setItem(markerKey(scope), 'cookie');
    if (scope === 'staff') document.cookie = `token=${session.accessToken}; path=/; Max-Age=900; SameSite=Strict${location.protocol === 'https:' ? '; Secure' : ''}`;
    if (login) {
        securityState.locked = false;
        securityState.identity = null;
        localStorage.removeItem('telegram-reminder:' + identityOf(next));
    }
    window.dispatchEvent(new CustomEvent('argus:session', {detail: {scope, session, login}}));
}
export function refreshSession(scope) {
    if (pending[scope]) return pending[scope];
    const run = async () => {
        const before = claims(scope);
        const response = await http.post(authBase(scope) + '/refresh', {});
        if (isUpdatePaused()) throw new Error('Дождитесь обновления приложения.');
        const current = claims(scope), next = decode(response.data.accessToken);
        if (!before || !current || before.sid !== current.sid) throw new Error('Вход изменён в другой вкладке.');
        if (identityOf(before) !== identityOf(next)) {
            lockSession(scope, null); throw new Error('Учётная запись изменилась. Войдите снова.');
        }
        acceptSession(scope, response.data);
        return response.data;
    };
    const work = () => exclusive(scope, run);
    pending[scope] = work().catch(error => {
        if ([401, 428].includes(error?.response?.status)) lockSession(scope, error);
        throw error;
    }).finally(() => { pending[scope] = null; });
    return pending[scope];
}
export async function securityRequest(scope, operation, data, method) {
    const publicOperation = /^(login|challenge\/|refresh|logout)/.test(operation);
    const token = claims(scope);
    if (!publicOperation && token && token.exp * 1000 < Date.now() + 30000) await refreshSession(scope);
    try {
        const request = async () => {
            const result = (await http.request({url: authBase(scope) + '/' + operation, method: method || (data === undefined ? 'get' : 'post'), data,
                headers: {Authorization: 'Bearer ' + (localStorage.getItem(tokenKey(scope)) || '')}})).data;
            if (result.accessToken) acceptSession(scope, result, true);
            return result;
        };
        return publicOperation ? await exclusive(scope, request) : await request();
    } catch (error) {
        if (!publicOperation && ['AUTH_IDLE', 'AUTH_FACTOR_REQUIRED', 'AUTH_BOT_BLOCKED', 'AUTH_SESSION_EXPIRED', 'AUTH_CREDENTIALS_CHANGED', 'AUTH_ACCOUNT_DISABLED'].includes(error?.response?.data?.code)) lockSession(scope, error);
        throw error;
    }
}
export function installSessionInterceptor(client, scope = 'staff') {
    return client.interceptors.response.use(response => response, async error => {
        const request = error.config || {}, code = error?.response?.data?.code || '';
        if (/\/auth\/(login|challenge|stepup|settings|accounts)/.test(request.url || '') || String(request.url || '').startsWith('/auth/login')) throw error;
        if (error?.response?.status === 401 && (code === 'AUTH_ACCESS_EXPIRED' || error.response.data === 'jwt expired') && !request._securityRetry) {
            request._securityRetry = true;
            const session = await refreshSession(scope);
            if (isUpdatePaused()) throw error;
            request.headers = Object.assign({}, request.headers, {Authorization: 'Bearer ' + session.accessToken});
            return client(request);
        }
        if (['AUTH_IDLE', 'AUTH_FACTOR_REQUIRED', 'AUTH_BOT_BLOCKED', 'AUTH_SESSION_EXPIRED', 'AUTH_CREDENTIALS_CHANGED', 'AUTH_ACCOUNT_DISABLED'].includes(code)) lockSession(scope, error);
        throw error;
    });
}
export async function logoutSession(scope = 'staff') {
    await securityRequest(scope, 'logout', {});
    localStorage.removeItem(tokenKey(scope));
    localStorage.removeItem(markerKey(scope));
    if (scope === 'staff') document.cookie = 'token=; path=/; Max-Age=0';
    securityState.locked = false;
    securityState.identity = null;
}
export const securityReasons = {
    admin: 'Admin компании', platform_admin: 'Администратор платформы', access_management: 'Управление аккаунтами и доступом',
    employee_registry: 'Полный кадровый реестр', employee_personal: 'Личные сведения по всей компании', employee_employment: 'Кадровые сведения по всей компании',
    employee_characteristics: 'Характеристики по всей компании', payroll: 'Расчёты и зарплата', global_workforce: 'Кадровая доска всей компании', global_timesheets: 'Табели всей компании',
    global_objects: 'Все объекты компании', mechanic_registry: 'Полный реестр техники', fuel_card_registry: 'Полный реестр топливных карт',
    global_gallery: 'Галерея всей компании', global_analytics: 'Аналитика всей компании', global_reports: 'Сменные отчёты всей компании',
    catalog_management: 'Управление общими справочниками', manual: 'Персональное требование Admin'
};
